import './condition';

// Import dependencies
import { html, LitElement } from 'lit';
import { property } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';

// Get translatable strings
const strings = UncannyAutomatorBackend.logs.components.logItemFilterBlock.i18n;

// eslint-disable-next-line require-jsdoc
const LogActionFilterMixin = ( superClass ) => class extends superClass {
	// Properties
	// The filter ID, unique to each filter block
	@property( { type: String, attribute: 'filter-id' } ) accessor id;

	// The ID of the current item opened
	@property( { type: String, attribute: 'sidebar-source-id' } ) accessor sidebarSourceID;

	// The logic of the conditions (any/all)
	@property( { type: String } ) accessor logic;

	// Whether the conditions were passed or not
	@property( { type: Boolean } ) accessor passed;

	// Conditions
	@property( { type: Array, attribute: false } ) accessor conditions = [];

	// The items inside this filter block
	@property( { type: Array, attribute: false } ) accessor items = [];

	// This property is used to indicate to the web component that it shouldn't try to retrieve data from the server
	// and that it should use the data provided when it was created
	@property( { type: Boolean, attribute: 'import-mode' } ) accessor isImportMode = false;

	// eslint-disable-next-line require-jsdoc
	render() {
		return html`
			<div class="filter-block">
				${ this._templateHeader }
				${ this._templateItems }
			</div>
		`;
	}

	/**
	 * Creates the header of the filter block
	 * This includes the logic and the conditions
	 *
	 * @return {TemplateResult} The header
	 */
	get _templateHeader() {
		return html`
			<div class="filter-block-header">
				${ this._templateLogic }
				${ this._templateConditions }
			</div>
		`;
	}

	/**
	 * Creates the header that shows the logic of the filter block
	 *
	 * @return {TemplateResult} The logic
	 */
	get _templateLogic() {
		return html`
			<div class="logic">
				<div class="logic__condition-icon">
					<uo-icon id="code-merge"></uo-icon>
				</div>
				
				<div class="logic__condition">${ strings.runIf }</div>

				${
					this.conditions.length > 1 ?
					html`
						<uo-chip>${ this.logic === 'all' ? strings.all : strings.any }</uo-chip>
						<div class="logic__sentence">${ this.logic === 'all' ? strings.allFull : strings.anyFull }</div>
					` : ''
				}
			</div>
		`;
	}

	/**
	 * Creates the template that shows the conditions
	 *
	 * @return {TemplateResult} The conditions
	 */
	get _templateConditions() {
		return html`
			<div class="conditions">
				${ repeat(
					this.conditions,
					item => item.id,
					condition => html`
						<uap-log-filter-condition
							condition-id=${ condition.id }
							condition-code=${ condition.code }

							integration-code=${ condition.integration_code }

							status-id=${ condition.status_id }

							?is-open=${ this.sidebarSourceID === `condition-${ condition.id }` }

							.resultMessage=${ condition.result_message }
							.titleHTML=${ condition.title_html }
							.fields=${ condition.fields }
						></uap-log-filter-condition>
					`
				) }
			</div>
		`;
	}

	/**
	 * Creates the section that shows all the actions
	 *
	 * @return {TemplateResult} The actions
	 */
	get _templateItems() {
		return html`
			<div class="filter-block-items">
				${ repeat(
					this.items,
					item => item.id,
					item => html`
						<uap-log-item-action 
							item-id=${ item.id }
							item-log-id=${ item.item_log_id }
							item-code=${ item.code }
							
							integration-code=${ item.integration_code }

							status-id=${ item.status_id }

							start-date=${ item.start_date }
							end-date=${ item.end_date }

							.resultMessage=${ item.result_message }
							
							.titleHTML=${ item.title_html }
							.fields=${ item.fields }
							.runs=${ item.runs }

							?is-open=${ this.sidebarSourceID === `item-${ item.id }` }
							?can-rerun=${ item.can_rerun }

							?from-legacy-log=${ item.from_legacy_log }
							?import-mode=${ this.isImportMode }
						></uap-log-item-action>
					`
				) }
			</div>
		`;
	}
}

export const AutomatorActionFilterBase = LogActionFilterMixin( LitElement );
